import { IconAlertTriangle } from '@tabler/icons-react';
import { useAuthStore } from '../../lib/auth.js';
import { Button } from '../ui/Button.js';
import { useBillingStatus, useOpenPortal } from '../../hooks/useBilling.js';

/**
 * Non-blocking warning shown in the app shell when the agency's subscription is
 * past_due — payment failed but they keep full access during Stripe's grace
 * period (hasFullAccess treats past_due as full). It prompts the admin to fix
 * their card via the Stripe portal before they get cut off.
 *
 * Only agency admins see it (they're the ones who can fix billing); consultants
 * aren't nagged, and super-admins never fetch /billing (it 400s for no-tenant).
 * It clears automatically once the payment resolves (status → active on the next
 * /billing refetch). Mounted after the suspended / billing-block guards, so a
 * suspended or blocked agency sees their full-screen screen instead of this.
 */
export function PastDueBanner() {
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';
  const { data } = useBillingStatus(isAdmin);
  const portal = useOpenPortal();

  if (!isAdmin || data?.status !== 'past_due' || data.access !== 'full') return null;

  return (
    <div
      className="mb-6 flex flex-wrap items-center gap-3 rounded-lg px-4 py-3 text-sm"
      style={{ backgroundColor: '#FCEFDC', color: '#B85C00', border: '1px solid #F2DCB8' }}
    >
      <IconAlertTriangle size={18} className="shrink-0" />
      <span className="flex-1">
        Your last payment failed — please update your card to avoid losing access.
      </span>
      <Button
        size="sm"
        variant="secondary"
        loading={portal.isPending}
        onClick={() => portal.mutate()}
      >
        Update payment
      </Button>
    </div>
  );
}
